import { EventSourcePolyfill } from 'event-source-polyfill'
import { parseMercureEvent } from './eventEnvelope'
import { calcReconnectDelay } from './backoff'
import type {
  MercureEvent,
  MercureConfig,
  ConnectionMetrics,
  ConnectionStatus,
  EventCallback,
  UnsubscribeFunction,
} from './types'

export class MercureClient {
  private eventSource: EventSourcePolyfill | null = null
  private listeners: Map<string, Set<EventCallback>> = new Map()
  private status: ConnectionStatus = 'disconnected'
  private reconnectAttempts = 0
  private metrics: ConnectionMetrics = {
    connectedAt: new Date(),
    lastHeartbeat: new Date(),
    messagesReceived: 0,
    reconnectCount: 0,
    latency: 0,
    averageLatency: 0,
    messagesSent: 0,
  }

  constructor(private config: MercureConfig) {}

  async connect(): Promise<void> {
    if (this.eventSource) {
      this.disconnect()
    }

    this.status = 'connecting'

    try {
      const url = new URL(this.config.hubUrl)

      this.config.topics.forEach((topic) => {
        url.searchParams.append('topic', topic)
      })

      this.eventSource = new EventSourcePolyfill(url.toString(), {
        headers: {
          Authorization: `Bearer ${this.config.jwtToken ?? ''}`,
        },
        heartbeatTimeout: this.config.heartbeatTimeout ?? 90000,
        withCredentials: false,
      })

      this.setupEventHandlers()
    } catch (error) {
      console.error('Failed to connect to Mercure:', error)
      this.status = 'error'
      this.handleReconnect()
      throw error
    }
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    this.status = 'disconnected'
  }

  subscribe(eventType: string, callback: EventCallback): UnsubscribeFunction {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }

    this.listeners.get(eventType)!.add(callback)

    return () => {
      const listeners = this.listeners.get(eventType)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.listeners.delete(eventType)
        }
      }
    }
  }

  getConnectionStatus(): ConnectionStatus {
    return this.status
  }

  getMetrics(): ConnectionMetrics {
    return { ...this.metrics }
  }

  async reconnect(): Promise<void> {
    this.reconnectAttempts = 0
    await this.connect()
  }

  updateToken(newToken: string): void {
    this.config.jwtToken = newToken
    if (this.status === 'connected') {
      this.connect().catch(console.error)
    }
  }

  addTopic(topic: string): void {
    if (!this.config.topics.includes(topic)) {
      this.config.topics.push(topic)
      // `connect()` resolves as soon as EventSource is created, not when its
      // `onopen` fires. A route can therefore select a chat while the initial
      // connection is still `connecting`; waiting for `connected` here leaves
      // that EventSource subscribed to the old set of topics forever.
      if (this.eventSource) {
        this.connect().catch(console.error)
      }
    }
  }

  removeTopic(topic: string): void {
    const index = this.config.topics.indexOf(topic)
    if (index > -1) {
      this.config.topics.splice(index, 1)
      if (this.eventSource) {
        this.connect().catch(console.error)
      }
    }
  }

  private setupEventHandlers(): void {
    if (!this.eventSource) return

    this.eventSource.onopen = () => {
      this.status = 'connected'
      this.reconnectAttempts = 0
      this.metrics.connectedAt = new Date()
      this.notifyStatusChange()
    }

    this.eventSource.onmessage = (event: MessageEvent) => {
      let raw: unknown
      try {
        raw = JSON.parse(String(event.data))
      } catch (error) {
        console.error('Error parsing Mercure message:', error)
        return
      }

      const parsed = parseMercureEvent(raw)
      if (!parsed) {
        console.warn('Dropped malformed Mercure event payload')
        return
      }

      this.handleMessage(parsed)
      this.metrics.messagesReceived++
      this.metrics.lastHeartbeat = new Date()
    }

    this.eventSource.onerror = (_event: Event) => {
      this.status = 'error'
      this.notifyStatusChange()
      this.handleReconnect()
    }
  }

  private handleMessage(data: MercureEvent): void {
    const listeners = this.listeners.get(data.type)
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error('Error in event callback:', error)
        }
      })
    }

    const wildcardListeners = this.listeners.get('*')
    if (wildcardListeners) {
      wildcardListeners.forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error('Error in wildcard callback:', error)
        }
      })
    }
  }

  private handleReconnect(): void {
    const maxAttempts = this.config.reconnectAttempts ?? 5
    if (this.reconnectAttempts >= maxAttempts) {
      console.error('Max reconnection attempts reached')
      this.status = 'error'
      this.notifyStatusChange()
      return
    }

    this.status = 'reconnecting'
    this.reconnectAttempts++
    this.metrics.reconnectCount++

    const delay = calcReconnectDelay(this.reconnectAttempts, this.config.reconnectDelay ?? 1000)

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error('Reconnection failed:', error)
      })
    }, delay)
  }

  private notifyStatusChange(): void {
    const statusListeners = this.listeners.get('status_change')
    if (statusListeners) {
      const event: MercureEvent = {
        type: 'status_change',
        data: { status: this.status },
        timestamp: new Date(),
      }

      statusListeners.forEach((callback) => {
        try {
          callback(event)
        } catch (error) {
          console.error('Error in status change callback:', error)
        }
      })
    }
  }
}
